import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Switch } from '../../ui/switch';
import { Label } from '../../ui/label';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Loader2, Plus, Pencil, Trash2, ListChecks } from 'lucide-react';
import { purchasingAPI, ReasonCode } from '../../../lib/api';
import { toast } from 'sonner';

const CATEGORY_OPTIONS = [
  { value: 'delay', label: 'Delay' },
  { value: 'quality', label: 'Quality Issue' },
  { value: 'quantity', label: 'Quantity Change' },
  { value: 'price', label: 'Price Change' },
  { value: 'date_change', label: 'Date Change' },
  { value: 'technical', label: 'Technical Issue' },
  { value: 'supplier', label: 'Supplier Issue' },
  { value: 'customer', label: 'Customer Issue' },
  { value: 'other', label: 'Other' },
];

const CATEGORY_LABELS: Record<string, string> = Object.fromEntries(
  CATEGORY_OPTIONS.map(o => [o.value, o.label])
);

interface FormState {
  code: string;
  description: string;
  category: string;
  is_active: boolean;
}

export function ReasonCodesSection() {
  const [codes, setCodes] = useState<ReasonCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ReasonCode | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<FormState>({ code: '', description: '', category: '', is_active: true });
  const [deleteTarget, setDeleteTarget] = useState<ReasonCode | null>(null);

  const fetchCodes = useCallback(async () => {
    try {
      setLoading(true);
      const res = await purchasingAPI.getReasonCodes({ page_size: 200 });
      const data = res.data;
      setCodes(Array.isArray(data) ? data : (data as { results: ReasonCode[] }).results ?? []);
    } catch {
      toast.error('Failed to load reason codes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCodes(); }, [fetchCodes]);

  const openDialog = (rc?: ReasonCode) => {
    if (rc) {
      setEditing(rc);
      setForm({ code: rc.code, description: rc.description, category: rc.category, is_active: rc.is_active });
    } else {
      setEditing(null);
      setForm({ code: '', description: '', category: '', is_active: true });
    }
    setDialogOpen(true);
  };

  const sortCodes = (list: ReasonCode[]) =>
    [...list].sort((a, b) => a.category.localeCompare(b.category) || a.code.localeCompare(b.code));

  const saveCode = async () => {
    if (!form.code.trim() || !form.description.trim() || !form.category) {
      toast.error('Code, description, and category are required');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        const res = await purchasingAPI.updateReasonCode(editing.id, form);
        setCodes(prev => sortCodes(prev.map(c => (c.id === editing.id ? res.data : c))));
        toast.success('Reason code updated');
      } else {
        const res = await purchasingAPI.createReasonCode(form);
        setCodes(prev => sortCodes([...prev, res.data]));
        toast.success('Reason code created');
      }
      setDialogOpen(false);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: Record<string, string[]> } };
      const detail = axiosErr.response?.data;
      if (detail && typeof detail === 'object') {
        const msgs = Object.values(detail).flat().join(', ');
        toast.error(msgs || 'Failed to save reason code');
      } else {
        toast.error('Failed to save reason code');
      }
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const id = deleteTarget.id;
    const previous = codes;
    setCodes(prev => prev.filter(c => c.id !== id));
    setDeleteTarget(null);
    try {
      await purchasingAPI.deleteReasonCode(id);
      toast.success('Reason code deleted');
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number } };
      if (axiosErr.response?.status === 404) {
        toast.success('Reason code already removed');
      } else {
        setCodes(previous);
        toast.error('Failed to delete reason code');
      }
    }
  };

  const toggleActive = async (rc: ReasonCode) => {
    setCodes(prev => prev.map(c => c.id === rc.id ? { ...c, is_active: !c.is_active } : c));
    try {
      await purchasingAPI.updateReasonCode(rc.id, { is_active: !rc.is_active });
    } catch {
      toast.error('Failed to update reason code');
      await fetchCodes();
    }
  };

  if (loading && codes.length === 0) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <ListChecks className="h-5 w-5" />
                Reason Code (RCC)
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Reason codes appear in change request, decline, and hold workflows
              </p>
            </div>
            <Button size="sm" onClick={() => openDialog()}>
              <Plus className="h-4 w-4 mr-1" />
              Add Reason Code
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {codes.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <ListChecks className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No reason codes yet</p>
              <p className="text-sm mt-1">Add reason codes so users can select them in workflows</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead className="w-32 text-center">Status</TableHead>
                  <TableHead className="w-28 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {codes.map(rc => (
                  <TableRow key={rc.id}>
                    <TableCell className="font-medium">{rc.code}</TableCell>
                    <TableCell>{rc.description}</TableCell>
                    <TableCell>{CATEGORY_LABELS[rc.category] || rc.category}</TableCell>
                    <TableCell className="text-center">
                      <div className="inline-flex items-center gap-2">
                        <Badge
                          variant={rc.is_active ? 'default' : 'secondary'}
                          className={rc.is_active ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' : ''}
                        >
                          {rc.is_active ? 'Enabled' : 'Disabled'}
                        </Badge>
                        <Switch checked={rc.is_active} onCheckedChange={() => toggleActive(rc)} />
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openDialog(rc)}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => setDeleteTarget(rc)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Reason Code' : 'New Reason Code'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Code</Label>
              <Input value={form.code} onChange={e => setForm({ ...form, code: e.target.value })} placeholder="e.g. DEL01" />
            </div>
            <div>
              <Label>Description</Label>
              <Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="e.g. Material shortage" />
            </div>
            <div>
              <Label>Category</Label>
              <select
                value={form.category}
                onChange={e => setForm({ ...form, category: e.target.value })}
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
              >
                <option value="">Select category...</option>
                {CATEGORY_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center justify-between">
              <Label>Status</Label>
              <div className="inline-flex items-center gap-2">
                <span className="text-sm text-muted-foreground">{form.is_active ? 'Enabled' : 'Disabled'}</span>
                <Switch checked={form.is_active} onCheckedChange={checked => setForm({ ...form, is_active: checked })} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveCode} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editing ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={open => { if (!open) setDeleteTarget(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Confirm Delete</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Delete reason code <strong>"{deleteTarget?.code}"</strong>? This cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={confirmDelete}>
              <Trash2 className="h-4 w-4 mr-2" /> Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
